import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { ktx2Extend } from '../utils/ktx2';
import { freeGltf } from '../utils/gltfMemory';
import { detectFloor } from '../utils/venueFloor';
import { ROOMS } from '../rooms';
import { useWorldStore } from '../state/worldStore';
import { assetUrl } from '../services/assets';
import { IS_MOBILE } from '../config';
import { loadRoomConfig } from '../services/worldObjects';
import { setInteriorFloor, setRoomSpace } from '../state/interiorFloor';
import { useVenueAnchors } from '../hooks/useVenueAnchors';
import InteriorResidents from './InteriorResidents';
import RoomStory from './RoomStory';
import RoomGuests from './RoomGuests';
import VenueSetList from './VenueSetList';
import ListeningSeats from './ListeningSeats';
import EnsembleDirector from './EnsembleDirector';
import Portals from './Portals';
import { RoomPlacements } from './PlacementLayer';
import RoomMarkers from './RoomMarkers';
import VenueScreens from './VenueScreens';
import VenueStage from './VenueStage';
import RoomHost from './RoomHost';
import GymFloor from './GymFloor';
import TableHeadphones from './TableHeadphones';
import CafeGuests from './CafeGuests';
import FrontDesk from './FrontDesk';
import RoomGreeting from './RoomGreeting';
import PrintShopScreen from './printshop/PrintShopScreen';
import Elevator from './hypeu/Elevator';
import Classroom from './hypeu/Classroom';
import NearSeats from './NearSeats';
import RoomDecals from './RoomDecals';
import RoomHostSign from './RoomHostSign';
import CafeCounter from './CafeCounter';
import CounterItems from './CounterItems';
import { applyRoomTextures } from '../services/roomTextures';
import { baseGroundY, onGround } from '../city/cityGround';
import { siteKey, townSited } from '../services/roomSpace';

// MeshInterior — one GLB venue, loaded when you walk in and released when you
// leave. The generic path for "experience areas": a subway, a grocery store, a
// loft. Adding one is a rooms.js entry with a `model`, not a new component.
//
// LOADED ON ENTRY, FREED ON EXIT — the rule that makes a lot of these possible.
//
// These are big: the grocery store is 7.3MB and 180k triangles, the metro 10.3MB
// and 153k. A handful shipped with the world would be tens of megabytes nobody
// waiting at the spawn point has any use for, and — the part that actually bites
// — every one still mounted holds its geometry and textures on the GPU. Download
// is a one-time cost the HTTP cache forgives; VRAM is charged for as long as the
// thing exists, and iOS answers a full GPU by killing the tab.
//
// So the component mounts only while its room is the active interior, and its
// unmount hands the memory back. Coming back re-decodes from the HTTP cache
// rather than re-downloading. This is the same trade CityModel already makes on
// mobile when a splat room needs the headroom.
// Bounds that are correct for an INSTANCED model.
//
// Box3.setFromObject walks matrixWorld, which for an InstancedMesh describes the
// batch node — not where its copies actually are. The grocery store is 3,252
// copies in 109 batches (that instancing is what took it from 3,598 draw calls
// to a workable number), so measuring it the ordinary way describes almost none
// of the building. Grounding off that put the floor in the wrong place, which is
// how a shop ends up in the sky.
//
// So: union each geometry's own box through every instance matrix it is drawn
// with, and fall back to the plain path for ordinary meshes.
function measureBounds(root) {
  const box = new THREE.Box3();
  const tmp = new THREE.Box3();
  const mat = new THREE.Matrix4();
  root.traverse((o) => {
    if (!o.isMesh || !o.geometry) return;
    if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
    const gb = o.geometry.boundingBox;
    if (!gb) return;
    if (o.isInstancedMesh && o.instanceMatrix) {
      for (let i = 0; i < o.count; i++) {
        o.getMatrixAt(i, mat);
        mat.premultiply(o.matrixWorld);
        box.union(tmp.copy(gb).applyMatrix4(mat));
      }
    } else {
      box.union(tmp.copy(gb).applyMatrix4(o.matrixWorld));
    }
  });
  return box;
}

// WHERE THE FLOOR ACTUALLY ENDED UP, measured on the RENDERED object.
//
// Every calculation here says a resident at local y=0 stands on the floor, and
// the room says otherwise. When the arithmetic and the room disagree, the
// arithmetic is describing a tree that is not the one on screen — so this asks
// the scene graph directly, after mount, once.
//
// It reports the world Y that residents sit at against the world Y the model
// actually occupies. Those two disagreeing IS the bug, and the difference
// between them is the offset that fixes it.
function FloorProbe({ model, roomId }) {
  const ref = useRef();
  useEffect(() => {
    const t = setTimeout(() => {
      if (!ref.current || !model) return;
      ref.current.updateWorldMatrix(true, false);
      const residentY = new THREE.Vector3().setFromMatrixPosition(ref.current.matrixWorld).y;
      model.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(model);
      console.log(
        '[venue] ' + roomId + ' FLOOR PROBE — a body at local y=0 renders at world y '
        + residentY.toFixed(3) + '; the model spans world y ' + box.min.y.toFixed(3)
        + '..' + box.max.y.toFixed(3) + ' — so the body is '
        + (residentY - box.min.y).toFixed(3) + 'u above the model bottom.'
      );
    }, 500);
    return () => clearTimeout(t);
  }, [model, roomId]);
  return <group ref={ref} />;
}

// ── THE RESERVED MIRROR (Khari, 2026-08-03: "there are 4 mirrors here — we
// can reserve one for viewing yourself, the one next to the seat… looking
// in this mirror will always show you yourself, even when you come back
// later"). Station 1's glass (nearest the wash chair; make_salon.mjs tags
// every pane with userData {affordance:'mirror', station}) is painted with
// the player's CURRENT look image whenever they're in the salon — fresh
// from the scene when Sassy just cut it, from the profile on any later
// walk-in. Cover-cropped and X-FLIPPED, because a mirror shows you
// mirrored. Signed URL in a WebGL texture is fine; only fetch() is
// CORS-walled.
function SalonMirrorSkin({ model }) {
  const salonLookUrl = useWorldStore((s) => s.salonLookUrl);
  const setSalonLookUrl = useWorldStore((s) => s.setSalonLookUrl);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);

  // Walk-in fill: no look in the store yet → your profile face IS your look.
  useEffect(() => {
    if (salonLookUrl || !myAvatarId) return undefined;
    let dead = false;
    import('../services/api').then(({ api }) => api.avatarDetail(myAvatarId))
      .then((d) => {
        if (dead) return;
        const av = d?.avatar || d;
        if (av?.profile_image_url) setSalonLookUrl(av.profile_image_url);
      })
      .catch(() => { /* the glass stays dark — honest for a face that doesn't exist yet */ });
    return () => { dead = true; };
  }, [salonLookUrl, myAvatarId, setSalonLookUrl]);

  useEffect(() => {
    if (!salonLookUrl || !model) return undefined;
    let target = null;
    model.traverse((o) => {
      if (!target && o.isMesh && o.userData?.affordance === 'mirror' && Number(o.userData.station) === 1) target = o;
    });
    if (!target) return undefined;
    let dead = false;
    const prev = target.material;
    let mat = null;
    new THREE.TextureLoader().load(salonLookUrl, (tex) => {
      if (dead) return;
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.wrapS = THREE.RepeatWrapping;
      // Cover-crop to the pane (0.92w × 1.62h in the descriptor) + X flip.
      const glassAspect = 0.92 / 1.62;
      const img = tex.image;
      const imgAspect = img && img.height ? img.width / img.height : glassAspect;
      let rx = 1, ry = 1, ox = 0, oy = 0;
      if (imgAspect > glassAspect) { rx = glassAspect / imgAspect; ox = (1 - rx) / 2; }
      else { ry = imgAspect / glassAspect; oy = (1 - ry) / 2; }
      tex.repeat.set(-rx, ry);
      tex.offset.set(ox + rx, oy);
      mat = new THREE.MeshBasicMaterial({ map: tex, toneMapped: false });
      target.material = mat;
    });
    return () => {
      dead = true;
      if (target && prev) target.material = prev;
      if (mat) { mat.map?.dispose(); mat.dispose(); }
    };
  }, [salonLookUrl, model]);

  return null;
}

export default function MeshInterior({ roomId }) {
  const room = ROOMS[roomId];
  const lastLog = useRef('');
  // SITED vs VIRTUAL. A public place belongs to the city: it stands inside the
  // building that fronts it, at that building's coordinates, and its windows
  // look out on the real street. A private one — an apartment, a rehearsal
  // room — is instanced at the origin with the world hidden, because there is
  // no single place in the city it "is", and many people may be in their own
  // copy of it at once.
  //
  // `placement: 'sited'` puts the room at its venue anchor. Everything else
  // stays at the origin, which is what every room has done until now.
  const anchors = useVenueAnchors();
  // (Its own site, or — a Hype-U classroom — the lobby's: services/roomSpace siteKey.)
  const site = siteKey(roomId);
  const anchor = room.placement === 'sited' ? anchors?.[site] : null;
  // The height nudge from the venue tool, applied to the INTERIOR as well as the
  // shell. Both auto-ground their model by dropping its lowest point to zero, so
  // both inherit the same wrong answer when that point is a foundation rather
  // than the floor — and correcting only the outside would leave the shell
  // sitting on the street with its interior floor still a few feet under it.
  const yNudge = useWorldStore((s) => (
    s.venuePlaceDraft?.[site]?.y ?? s.venueAnchorOverrides?.[site]?.y ?? 0
  ));
  // WHICH LEVEL YOU ARRIVE ON.
  //
  // Auto-grounding drops the model's lowest surface to your feet, which is right
  // for a single-storey room and wrong for anything with a level below the one
  // it grounds on. The metro grounds on its upper concourse — the top of the
  // stairs — while the platform is underneath, and the entrance model has
  // already walked you down those stairs by the time you get here. So the room
  // has to move UP until the level you should be standing on reaches your feet.
  //
  // A saved decision plus a live tune, like the fit: `roomY` persists, `yTune`
  // is what you are dragging right now.
  // SAVED SETTINGS BEAT AUTHORED ONES. rooms.js is the default a room ships
  // with; the row is what someone stood in it and decided. Declared here rather
  // than beside its loader below, because the origin needs it and a `const` read
  // before its declaration is a runtime throw the build cannot see.
  // A REGISTRY instance (venueTypes) is its own room id with no row of its own
  // yet, so it reads its base room's — a new club is the Warehouse as it was
  // tuned in-world, not the rooms.js fallback the Warehouse itself outgrew.
  const cfg = useWorldStore((s) => s.roomConfigs?.[roomId] ?? (room.venueBase ? s.roomConfigs?.[room.venueBase] : undefined));
  const yTune = useWorldStore((s) => s.roomYTune?.[roomId]) ?? 0;
  // A sited venue the town built stands on the street's ground (roomSpace
  // sitedLift), re-read once the ground's real heights arrive.
  const [, setGroundTick] = useState(0);
  useEffect(() => (townSited(room) ? onGround(() => setGroundTick((n) => n + 1)) : undefined), [room.registry, room.placement]); // eslint-disable-line react-hooks/exhaustive-deps
  // …and a floor of a shared building (a Hype-U classroom) that much higher (`floorY`).
  const lift = townSited(room) && anchor ? baseGroundY(anchor[0], anchor[2]) + 0.004 + (Number(room.floorY) || 0) : 0;
  const originY = yNudge + (cfg?.y || 0) + yTune + lift;
  const origin = anchor ? [anchor[0], originY, anchor[2]] : [0, originY, 0];

  // THE PLACEMENT SCALE, set on the slider in the venue tool and saved with the
  // anchor. An unsaved draft wins over the saved value, so dragging the slider
  // resizes the room you are standing in immediately rather than after a save
  // and a reload — which is the only way to judge whether a size is right.
  const overrides = useWorldStore((s) => s.venueAnchorOverrides);
  const drafts = useWorldStore((s) => s.venuePlaceDraft);
  const placeScale = drafts?.[roomId]?.scale ?? overrides?.[roomId]?.scale ?? 1;
  // LIVE FIT TUNING, applied as a group scale rather than folded into the fit.
  //
  // The fit memo clones the whole scene, so a live-dragged value in there would
  // re-clone hundreds of thousands of triangles per press. This rides the same
  // path the placement slider already uses — a plain scale on a group whose
  // origin is the room's centre with the floor at y=0 — so both stay true while
  // it moves, and the number it arrives at is bakeable straight into `fitTo`.
  //
  // Per room, because these models come from unrelated sources and each is wrong
  // in its own way. A single metres-per-unit constant is a reasonable first
  // guess and a bad rule.
  const fitTune = useWorldStore((s) => s.roomFitTune?.[roomId]) ?? 1;

  const setRoomConfig = useWorldStore((s) => s.setRoomConfig);
  const setSpawnOverride = useWorldStore((s) => s.setSpawnOverride);
  useEffect(() => {
    let dead = false;
    loadRoomConfig(roomId).then((c) => {
      if (dead || !c) return;
      setRoomConfig(roomId, c);
      // Deliberately NOT pushed in as a spawnOverride. RoomConfigs has already
      // loaded every room's row before you could enter one, and PlanetScene
      // reads the spawn from there directly — setting an override here would
      // arrive after the camera had settled and yank it, which is the same race
      // this replaced, just later.
      console.log(`[venue] ${roomId}: loaded saved settings — ${['fitTo', 'spawn', 'walk'].filter((k) => c[k]).join(', ') || 'nothing set'}`);
    });
    return () => { dead = true; };
  }, [roomId, setRoomConfig, setSpawnOverride]);

  // WHICH WAY THE SHOPFRONT FACES, tunable in-world.
  //
  // A virtual room can be authored facing anywhere, because you arrive looking at
  // whatever its spawn points at. A SITED one cannot: it has to agree with the
  // street it stands on, and nothing in the geometry says which wall is the
  // front. The only way to find the number is to stand inside and turn it until
  // the windows face the road, so it is a live knob (?venuerot=, degrees) with
  // rooms.js `modelRotY` as where the answer gets baked.
  // Precedence, most specific first: an explicit URL override (debugging), then
  // the placement tool's unsaved draft, then the saved placement, then the
  // rooms.js default. Radians throughout — the panel does the degree maths, and
  // the DB column is radians too, so there is exactly one conversion point.
  //
  // A VIRTUAL registry instance ignores the street facing: its building's rotY
  // turns the front door and the sign (VenueFacades), but the room is at the
  // origin with its spawn authored unturned, and roomSpace already treats a
  // virtual room as unturned. Turning it would put the spawn in a wall.
  const virtualInstance = !!room.venueType && room.placement !== 'sited';
  const siteRotY = useWorldStore((s) => {
    const r = townSited(room) ? Number(s.venueRegistry?.[site]?.site?.rotY) : NaN;
    return Number.isFinite(r) ? r : null;
  });
  const rotY = useMemo(() => {
    const q = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get('venuerot'));
    if (Number.isFinite(q)) return (q * Math.PI) / 180;
    if (virtualInstance) return room.modelRotY ?? 0;
    // A sited venue the town built faces the way its site does (services/roomSpace sitedTurn).
    return drafts?.[site]?.rotY ?? overrides?.[site]?.rotY ?? siteRotY ?? room.modelRotY ?? 0;
  }, [room.modelRotY, drafts, overrides, site, virtualInstance, siteRotY]);
  const gl = useThree((s) => s.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);
  // Through assetUrl, so a room's `model` can be a file in public/ OR an object
  // in the world-models bucket — moving it later is a rooms.js edit, no redeploy.
  const url = useMemo(() => assetUrl(room.model), [room.model]);
  const { scene } = useGLTF(url, true, true, ktx2);

  const { model, fit, note } = useMemo(() => {
    const m = scene.clone(true);

    // STRIP THE MODEL'S OWN LIGHTS.
    //
    // A GLB can carry punctual lights (KHR_lights_punctual), and their intensity
    // is authored in real photometric units for whatever renderer the author
    // used. Those numbers do not survive the trip: the grocery store's two lamps
    // arrive at intensity 543 against the 7.5 of the practicals sized for this
    // room — seventy times over, which is why the shop was whitewashed no matter
    // what the scene lights or materials did.
    //
    // Rather than rescale a number we cannot interpret, the room's own rig is
    // authoritative: it is derived from the measured bounds, so it is predictable
    // in a 3m tunnel and a 30m shop alike. `room.keepModelLights` opts back in
    // for a venue whose authored lighting is worth having.
    let stripped = 0;
    if (!room.keepModelLights) {
      const doomed = [];
      m.traverse((o) => { if (o.isLight) doomed.push(o); });
      for (const l of doomed) { l.parent?.remove(l); stripped++; }
    }

    m.updateMatrixWorld(true);
    const box = measureBounds(m);
    const size = new THREE.Vector3(); box.getSize(size);
    const centre = new THREE.Vector3(); box.getCenter(centre);
    // The surface you stand on, when it can be found — otherwise the box's floor,
    // which is what this always used and is right for a model with no foundation
    // hanging below it.
    const floorY = detectFloor(m);
    const groundY = floorY ?? box.min.y;

    // AUTO-FIT, because these arrive at whatever scale their author used and the
    // spread is not subtle — the bus station measures 5,650 units across where
    // the loft measures 24. `fitTo` is the room's intended footprint in world
    // units; without it the model is trusted at its natural size, which is right
    // for anything already authored in metres.
    const longest = Math.max(size.x, size.z) || 1;
    // TUNE LIVE, BAKE THE NUMBER — the same loop the room's lighting went
    // through. Scale is not something to reason about from a bounding box: the
    // supermarket measures 33.6 tall, but most of that is roof structure nobody
    // stands under, so "fit the height to a plausible ceiling" and "fit the
    // floor to a plausible shop" give wildly different answers and only one of
    // them is right when you are standing in it.
    //
    //   ?venuefit=<units>   the footprint's longest span — overrides `fitTo`
    //   ?venuescale=<n>     a raw multiplier on whatever the above works out to,
    //                       for when the model's bounds are the misleading part
    //
    // Read from the URL at memo time rather than as component state: changing
    // either one is a reload, and this memo clones the whole scene.
    const qp = (k) => {
      const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
      return Number.isFinite(v) ? v : null;
    };
    // URL knob → saved row → rooms.js. Same precedence the rest of the world
    // uses: a debugging override beats a decision, a decision beats a default.
    const fitTo = qp('venuefit') ?? cfg?.fitTo ?? room.fitTo;
    // BASE scale — everything authored: rooms.js `fitTo` plus the URL knobs.
    // The placement slider multiplies this, and is applied SEPARATELY below,
    // because the two must not be confused: the footprint published for the
    // venue brush has to be the base one. Publish the scaled size and the brush
    // would draw base x scale, you would nudge the slider, and the next visit
    // would publish that as the new base — a placement that grows every time you
    // walk in.
    // The placement slider is deliberately NOT in here. This memo clones the
    // whole scene, so folding a live-dragged value into it would re-clone 388k
    // triangles per slider tick. It is applied as a plain group scale at render
    // instead — the model group already centres the footprint and puts the floor
    // at y=0, so scaling that group about its own origin keeps both true.
    const s = (fitTo ? fitTo / longest : 1) * (qp('venuescale') ?? 1);

    // Report the AS-BUILT numbers, not the raw ones. The first version of this
    // logged the model's own units, so the grocery claimed to be "raised by
    // 32.69" when the scaled lift is 7.7 — a number you would then try to tune
    // against and get nowhere. What matters is the room you end up standing in:
    // its size in world units, and how tall it is against a 1.6 eye height.
    const head = size.y * s;
    // Built here, LOGGED in an effect below. Logging from inside the memo meant
    // one line per memo run rather than one per visit, which reads as a loop and
    // hides the thing worth noticing: this memo clones a 388k-triangle scene, so
    // if it ever does run repeatedly that is a real problem, not log noise.
    // What `venuefit` would have to be for a ~5u ceiling, which is about right
    // for a shop floor against a 1.6u eye. Offered rather than applied: this
    // model is nearly as tall as it is wide (33.6 against 29.5), which no
    // supermarket is, so its bounds are carrying something that is not the room
    // — and a suggestion derived from those bounds inherits that. It is a
    // starting number to look at, not an answer.
    const suggest = (5 / (size.y || 1)) * longest;
    const note = `[venue] ${roomId}: ${size.toArray().map((v) => v.toFixed(1)).join(' x ')} source`
      + `${s !== 1 ? ` → x${s.toFixed(3)} → ${(size.x * s).toFixed(1)} x ${head.toFixed(1)} x ${(size.z * s).toFixed(1)}` : ' (natural scale)'}`
      + ` · ceiling ${head.toFixed(1)}u vs 1.6u eye`
      + (head < 2.4 ? '  ← LOW: eye height is close to the ceiling, try ?venuefit=' : '')
      + (head > 12 ? `  ← TALL: you will feel doll-sized in here. Try ?venuefit=${suggest.toFixed(1)} for a ~5u ceiling, then bake it as fitTo.` : '')
      + (stripped ? ` · removed ${stripped} light(s) baked into the model` : '')
      // Say which reference the floor was set from. "The room is a bit high" is
      // otherwise indistinguishable from "the model has a foundation under it",
      // and only one of those is worth nudging by hand.
      + (floorY != null
        ? ` · floor found at ${floorY.toFixed(2)} (box bottom ${box.min.y.toFixed(2)}${
          Math.abs(floorY - box.min.y) > 1e-3 ? `, so ${((floorY - box.min.y) * s).toFixed(2)}u of foundation ignored` : ''})`
        : ` · no floor surface found — grounded on the box bottom (${box.min.y.toFixed(2)})`);

    // Centred on its own footprint with the floor at y=0, so a room's spawn
    // height means the same thing in every venue regardless of where its author
    // put the origin.
    return {
      model: m,
      // `size` is the room AS BUILT (post-scale) — the light rig sizes off this,
      // not the model's own units, or a fitTo'd venue would be lit for its
      // source scale.
      // `fitTo` as applied, so things authored in room space at a known fit
      // (a venue type's wall screens) can follow a saved or ?venuefit refit.
      fit: { s, offset: [-centre.x * s, -groundY * s, -centre.z * s], size: size.clone().multiplyScalar(s), fitTo: fitTo || null },
      note,
    };
    // `cfg?.fitTo` HAS TO BE IN HERE. This memo reads the saved fit, and without
    // it in the deps the room keeps whatever size it was built at when the row
    // arrives. That produced a very specific illusion: you tune the room, press
    // save, the live multiplier resets to 1 because the value is now baked — and
    // the model, never rebuilt, snaps back to the rooms.js default. The save had
    // worked perfectly every time; nothing was reading it.
  }, [scene, room.fitTo, cfg?.fitTo, room.keepModelLights, roomId]);

  // AS BUILT, including the placement slider. Everything that has to agree with
  // the room you are actually standing in — the light rig, the footprint the
  // plan view draws — sizes off this rather than off `fit.size`.
  const built = useMemo(
    () => fit.size.clone().multiplyScalar(placeScale),
    [fit.size, placeScale],
  );

  useEffect(() => () => freeGltf(model, url), [model, url]);

  // Hand the size to the store (and its localStorage cache), so the plan view
  // can draw this room's real footprint on the map without having to load a
  // venue GLB it is not standing in. This is the only place the number exists —
  // everything else in the app knows where a venue is, not how big it is.
  //
  // PUBLISHED AT BASE SCALE, without the placement slider, and the brush applies
  // the slider itself. Publishing the slid size would make the two feed on each
  // other: the brush would draw base x scale, you would nudge the slider, and
  // the next visit would record THAT as the new base — a venue that grows a
  // little every time you walk into it.
  const setVenueFootprint = useWorldStore((s) => s.setVenueFootprint);
  useEffect(() => {
    setVenueFootprint(roomId, [fit.size.x, fit.size.y, fit.size.z]);
  }, [roomId, fit.size, setVenueFootprint]);

  // WALLS THE CAMERA CANNOT WALK THROUGH.
  //
  // Splat rooms have had bounds since they shipped (Interior.jsx publishes them,
  // CameraBounds clamps to them); MESH rooms never published any, so every one
  // of them has been open world — walk far enough in the metro and you leave the
  // model entirely and stand in black.
  //
  // AUTHORED, NOT MEASURED, and deliberately so. The model's own footprint is
  // the wrong answer twice over: it includes the tunnel mouths, which have a
  // shell but NO FLOOR, so measuring would invite you to walk out over nothing;
  // and it cannot express a room whose middle is not walkable. So `walk` in
  // rooms.js is a decision, in the same world units as `spawn`, made once by
  // standing in the room — with `hole` for the case the perimeter is the only
  // walkable part.
  // WHAT YOU STAND ON. Published so FloorClamp can raycast the room's real
  // floor instead of anyone having to describe its outline. The fitted model,
  // not the raw scene: it has to be the object actually in the world, at the
  // size and height it is actually drawn.
  useEffect(() => {
    setInteriorFloor(model || null);
    return () => setInteriorFloor(null);
  }, [model]);

  // A SITED room's windows look at the REAL street (2026-10-31: The Jahva House
  // and HypeTunes HQ, like the Corner Market), so the night street their
  // builders painted behind the glass for the virtual room (rooms.js
  // `sitedHide`: jahva_night, street_view) is taken down while it stands in the
  // city — and only then: no anchor yet = the painting stays.
  const seesStreet = room.placement === 'sited' && !!anchor;
  useEffect(() => {
    const names = room.sitedHide || [];
    if (!model || !names.length) return undefined;
    const hit = [];
    model.traverse((o) => { if (names.includes(o.name)) hit.push(o); });
    for (const o of hit) o.visible = !seesStreet;
    return () => { for (const o of hit) o.visible = true; };
  }, [model, seesStreet, room.sitedHide]);

  // ROOM SPACE, published for the placement tool. The ghost is raycast in world
  // coordinates (it has to be — it is hit-testing the scene), so saving a
  // room-scoped surface means converting through this. Publishing the group
  // itself rather than its numbers keeps ONE source of truth: whatever this
  // group's transform is, that is the space, and no second copy can drift.
  const anchorRef = useRef();
  useEffect(() => {
    setRoomSpace(anchorRef.current || null);
    return () => setRoomSpace(null);
  });

  // TEXTURES, joined to the model by MATERIAL NAME (services/roomTextures).
  //
  // Applied here rather than baked into the GLB so the room stays pure geometry:
  // re-skinning a floor is a file swap, the maps cache separately from the model,
  // and the same room can wear a different material set per district later.
  //
  // Nothing is gated on it — the palette colour the model already carries IS the
  // fallback, so a map that never arrives leaves the untextured room rather than
  // a black one.
  const [, setSkinned] = useState(0);
  useEffect(() => {
    if (!model) return;
    applyRoomTextures(model, roomId, () => setSkinned((n) => n + 1));
    // SHADOWS WERE ALREADY ON — the Canvas enables them for desktop — but no
    // room mesh ever opted in, so nothing cast and nothing received and every
    // object floated a millimetre off the floor to the eye. Contact shadow is
    // the cue that puts an object IN a room rather than on top of a picture of
    // one, and it is a flag per mesh.
    if (!IS_MOBILE) {
      model.traverse((o) => {
        if (!o.isMesh) return;
        o.castShadow = true;
        o.receiveShadow = true;
      });
    }
  }, [model, roomId]);

  const setRoomBounds = useWorldStore((s) => s.setRoomBounds);
  const setBoundsEnabled = useWorldStore((s) => s.setBoundsEnabled);
  const walk = cfg?.walk ?? room.walk;
  // NO BOX BOUNDS FOR A MESH ROOM — the floor is the boundary.
  //
  // This published `walk` (a box, later a box with a hole, later two radii) and
  // every one of those shapes lost to the actual model: a station whose corridor
  // is a ring that is not a circle cannot be written down as any of them. Worse,
  // each attempt actively fought the room — a box measured before a resize
  // penned you into the middle, and a ring forced a circle onto a shape that
  // is not circular.
  //
  // FloorClamp answers the same question by raycasting the floor you are about
  // to step on, which is exact for any shape and costs one ray a frame. Two
  // mechanisms both claiming to own where you may walk is how the last several
  // rounds of this went, so this one stands down.
  //
  // Splat rooms keep CameraBounds: they have no geometry to raycast against.
  useEffect(() => {
    setRoomBounds(null);
    setBoundsEnabled(true);
  }, [roomId, setRoomBounds, setBoundsEnabled]);

  // One line per venue entry.
  //
  // WHERE it stands is logged HERE and not folded into `note`, because the
  // anchor arrives asynchronously — CityModel measures the buildings in an
  // effect — so a memo that read it would have to list it as a dependency, and
  // that memo clones a 388k-triangle scene. The choice is a stale log line or a
  // second full clone every time the anchors land; neither is acceptable, so the
  // part that changes late is reported from an effect that can afford to re-run.
  useEffect(() => {
    // WHAT YOU ARE ACTUALLY STANDING IN, and whether it is committed.
    //
    // `note` describes the BASE build, because that is all the fit memo knows —
    // the placement slider is applied at render. Logging that alone was actively
    // misleading: it would report "natural scale, ceiling 33.6u" while you stood
    // in a room scaled to 0.52 with a 17.5u ceiling, and every number you might
    // tune against was the wrong one.
    //
    // It also could not answer "is this saving?", which is not a question a
    // person should have to guess at — draft and saved look identical in the
    // world by design, since the whole point is to see the change before you
    // commit it. So the line says which it is.
    const draft = drafts?.[roomId];
    const saved = overrides?.[roomId];
    // Every field the tool can change has to be checked, or a venue that was
    // only rotated would report itself saved and quietly revert on reload.
    const dirty = !!draft && (
      (draft.x != null && (draft.x !== saved?.x || draft.z !== saved?.z))
      || (draft.scale != null && draft.scale !== (saved?.scale ?? 1))
      || (draft.rotY != null && draft.rotY !== (saved?.rotY ?? room.modelRotY ?? 0))
    );
    const where = room.placement === 'sited'
      ? ` · SITED at ${anchor ? `${anchor[0].toFixed(0)}, ${anchor[2].toFixed(0)}` : 'NO ANCHOR YET — at the origin until the city reports its buildings'}`
      : ' · virtual (at the origin)';
    // THE FINAL HEIGHT, stated outright.
    //
    // Everything else in this line describes the model; this describes where the
    // room ended up. The floor is grounded to y=0 by construction, so the ONLY
    // thing that can lift it is the manual nudge — and a nudge saved before the
    // floor detection existed is still being added on top of a grounding that no
    // longer needs it. Naming it turns "why is it floating" into one number.
    const lift = yNudge
      ? `  ⚠ FLOOR SITTING ${yNudge > 0 ? `${yNudge.toFixed(2)}u ABOVE` : `${Math.abs(yNudge).toFixed(2)}u BELOW`} the street — that is the saved Y nudge, not the model. Press 0 in the venue tool's Position row and Save.`
      : ' · floor at street level (no Y nudge)';
    const state = room.placement !== 'sited' ? ''
      : dirty ? '  ⚠ UNSAVED — press Save in the venue tool or this reverts on reload'
        : saved ? ' (saved)' : ' (from the sampler — nothing saved yet)';
    // Bake-ready number: fitTo is the footprint's longest span, so whatever the
    // slider has arrived at IS a fitTo. Offering it means the tuning can leave
    // the placement override and become the model's own default — with the
    // slider back at 1, since the two MULTIPLY and baking both would compound.
    const applied = placeScale !== 1
      ? ` · placement ×${placeScale.toFixed(2)} → ${built.x.toFixed(1)} x ${built.y.toFixed(1)} x ${built.z.toFixed(1)}`
        + ` · ceiling ${built.y.toFixed(1)}u vs 1.6u eye`
        + ` · to bake: fitTo: ${Math.max(built.x, built.z).toFixed(1)} in rooms.js, then set the slider back to ×1`
      : '';
    // DEDUPED ON CONTENT. This effect is keyed on several objects that are
    // rebuilt rather than mutated, so it can re-run without anything having
    // changed. The value-stable setters upstream fix the common cause; this
    // makes the log correct even when some future producer forgets to be.
    //
    // A repeated line is not a cosmetic problem — it is the only signal that
    // something is looping, so it has to stay meaningful.
    const msg = note + applied + where + state + lift;
    if (msg === lastLog.current) return;
    lastLog.current = msg;
    console.log(msg);
  }, [note, room.placement, anchor, placeScale, built, drafts, overrides, roomId]);

  // DROP THE CITY'S REFLECTION ENVIRONMENT WHILE INSIDE.
  //
  // PlanetScene builds a procedural <Environment> for the street — lightformers
  // standing in for sky and shop practicals — and it lands on `scene.environment`,
  // which is GLOBAL. Gating the JSX on `!inInterior` stops it being rebuilt but
  // does not reliably hand the slot back, so a venue was still being lit by an
  // outdoor IBL: flat, shadowless and bright on every untextured surface, which
  // is exactly the look that survived turning the sun off.
  //
  // Restored on exit so the street keeps its reflections.
  const threeScene = useThree((s) => s.scene);
  useEffect(() => {
    const prev = threeScene.environment;
    threeScene.environment = null;
    console.log(`[venue] ${roomId} lighting — ${prev ? 'cleared the street environment map' : 'no environment map was set'}`);
    return () => { threeScene.environment = prev; };
  }, [threeScene, roomId]);

  // What is ACTUALLY lighting this room, once. Guessing at this cost two rounds:
  // the sun was ruled out, the materials were ruled out, and the answer was a
  // global neither of those touched. So the room says what it is lit by.
  useEffect(() => {
    const t = setTimeout(() => {
      const found = [];
      threeScene.traverse((o) => { if (o.isLight && o.visible) found.push(`${o.type}(${o.intensity.toFixed(2)})`); });
      console.log(`[venue] ${roomId} lights: ${found.join(', ') || 'none'}`
        + ` · environment ${threeScene.environment ? 'SET' : 'none'}`);
    }, 400);
    return () => clearTimeout(t);
  }, [threeScene, roomId]);

  // THE ROOM'S OWN LIGHT, sized to the room.
  //
  // PlanetScene withholds the town's outdoor rig for these venues, so without
  // this a mesh interior is simply black. Derived from the measured bounds
  // rather than hand-placed, because the whole point of this component is that a
  // new venue is a data entry — a rig authored for a 30m shop would be wrong in
  // a 3m tunnel, and nobody would know until they walked in.
  //
  // Hemisphere for the fill (sky above, bounce below) plus practicals under the
  // ceiling, spread along the room's long axis so a deep space is not lit from
  // one end. `room.light` scales the whole thing if a venue reads dim or hot.
  const lights = useMemo(() => {
    const k = room.light ?? 1;
    // AS BUILT, so the rig follows the slider. The lights sit OUTSIDE the scaled
    // group on purpose — a pointLight's `distance` and `intensity` are plain
    // scalars that a parent's scale does not touch, so scaling the group they
    // were in would move them without changing their reach, and the room would
    // go dark as it grew. Sizing them off the built dimensions instead keeps the
    // inverse-square maths below honest at any scale.
    const w = built.x; const h = built.y; const d = built.z;
    const along = Math.max(w, d);

    // HANG THE LAMPS AT HUMAN HEIGHT, NOT AT THE MODEL'S CEILING.
    //
    // The first version put them at 80% of the measured height, which sounds
    // reasonable until a model's bounds include roof structure: the grocery
    // store measures 33.6 tall, so the lamps hung 26.9 units up with a reach of
    // 26.6 — they could not reach their own floor, and the room stayed dark
    // however far `light` was turned up. Capped to a plausible ceiling instead.
    // `room.lampHeight` overrides the derivation when a model's bounds mislead
    // it — a mezzanine or a roof lantern pushes `h` well above the ceiling you
    // actually stand under, and no cap is right for every building.
    const lamp = room.lampHeight ?? Math.min(Math.max(h * 0.6, 2.2), 7);

    // Intensity has to follow the INVERSE SQUARE, because decay is 2. A lamp at
    // 7 units needs roughly fifty times the intensity of one at 1 unit to land
    // the same light underfoot, so a fixed number is only ever right for one
    // room size. `distance` is the cutoff, kept well beyond the lamp height so
    // the falloff is not clipped before it reaches the ground.
    const intensity = lamp * lamp * 1.5;
    const reach = Math.max(along, lamp * 5);
    const n = along > 18 ? 3 : along > 9 ? 2 : 1;      // one practical per ~9 units
    const axisX = w >= d;
    return { k, lamp, intensity, reach, n, axisX, span: along };
  }, [built, room.light, room.lampHeight]);

  return (
    <group position={origin} rotation={[0, rotY, 0]} ref={anchorRef}>
      <hemisphereLight args={['#fff6e8', '#2a2620', 0.55 * lights.k]} />
      <ambientLight intensity={0.18 * lights.k} />
      {/* ONE KEY LIGHT, AND IT IS THE ONLY THING THAT CASTS.
          The practicals below are point lights, and a point light's shadow is a
          CUBE map — six renders each. Four of them casting is twenty-four extra
          passes per frame for an interior, which is not a trade worth making.
          A single spot from the ceiling is one pass and does the job, because
          what an interior needs from shadow is not direction, it is CONTACT:
          the dark under a chair leg that says the leg is on the floor. Angled a
          little off vertical so legs read as legs rather than as dots.
          Desktop only — mobile has shadows off at the Canvas anyway. */}
      {!IS_MOBILE && (
        <spotLight
          position={[built.x * 0.18, lights.lamp * 1.25, built.z * 0.14]}
          angle={1.05}
          penumbra={0.9}
          intensity={lights.intensity * lights.k * 1.6}
          distance={lights.reach * 2.2}
          decay={1.6}
          color="#fff1de"
          castShadow
          shadow-mapSize={[1024, 1024]}
          shadow-bias={-0.0012}
          shadow-normalBias={0.02}
        />
      )}
      {Array.from({ length: lights.n }, (_, i) => {
        // Spread across the long axis: centred when there is one, evenly spaced
        // from a quarter to three-quarters of the span otherwise.
        const t = lights.n === 1 ? 0 : (i / (lights.n - 1) - 0.5) * lights.span * 0.6;
        return (
          <pointLight
            key={i}
            position={[lights.axisX ? t : 0, lights.lamp, lights.axisX ? 0 : t]}
            intensity={lights.intensity * lights.k}
            distance={lights.reach}
            decay={2}
            color="#ffeedd"
          />
        );
      })}
      {/* THE PLACEMENT SLIDER, applied here rather than inside the fit memo —
          that memo clones the scene, and this value is dragged. The inner group
          already centres the footprint on the origin and puts the floor at y=0,
          so scaling it about that origin keeps both true at any size. */}
      {/* PEOPLE IN THE ROOM. Rendered here rather than only in Interior.jsx,
          which is the SPLAT room — a mesh venue had no resident layer at all,
          so placing one saved a row and drew nothing, and the tool looked
          broken rather than unimplemented.

          Inside the room group (so a SITED venue puts them at its address
          rather than at the world origin) but OUTSIDE the scale group: a
          resident is a person, sized against the world, not a fixture that
          shrinks when the building is fitted. */}
      <InteriorResidents roomId={roomId} />
      {/* A City Story scene playing IN this room (rooms.js storyMarks): the
          artists at their marks. Draws nothing in a room without marks. */}
      <RoomStory roomId={roomId} />
      {/* Artists IN here on their free time (API city/guestRooms): standing about
          on the room's guest marks, or on free floor found in this mesh. */}
      <RoomGuests roomId={roomId} />
      {/* A SET IN THIS ROOM (2026-10-30, API CityLifeService.ensureVenueSets):
          the performer at the booth or the spoken-word corner, the music and
          their words, the station held. Only a venue the town voted for with
          somewhere to perform (venueTypes `booth`, rooms.js `stage.spoken`).
          `k`: the mark is room space at the type's fit, like the screens below,
          so it follows a refit and the placement slider to stay behind the decks. */}
      {/* TONIGHT'S SETS, handwritten on a board by the door (VenueSetList):
          only where sets are booked — a town venue with somewhere to perform. */}
      {room.registry && (room.booth || room.stage?.spoken) && (
        <VenueSetList
          roomId={roomId}
          k={placeScale * fitTune * (fit.fitTo && room.screensFitTo ? fit.fitTo / room.screensFitTo : 1)}
        />
      )}
      {room.registry && (room.booth || room.stage?.spoken) && (
        <VenueStage
          roomId={roomId}
          k={placeScale * fitTune * (fit.fitTo && room.screensFitTo ? fit.fitTo / room.screensFitTo : 1)}
        />
      )}
      {/* A ROOM THAT IS SOMEBODY'S (2026-10-31, API city_rooms): its owner at
          the booth while the city has them in, else the sign with when they
          will be (rooms.js `booth` / `hostSign`). Both draw nothing in a room
          nobody owns. Same `k` as the set's: marks are room space at the fit. */}
      {(room.booth || room.hostSign) && (
        <>
          <RoomHost roomId={roomId} k={placeScale * fitTune * (fit.fitTo && room.screensFitTo ? fit.fitTo / room.screensFitTo : 1)} />
          <RoomHostSign roomId={roomId} k={placeScale * fitTune * (fit.fitTo && room.screensFitTo ? fit.fitTo / room.screensFitTo : 1)} />
        </>
      )}
      {/* Whoever is in the listening room's chairs. Mounted in the same space as
          the residents for the same reason — the seat poses are read off THIS
          model, so a sited venue seats people at its address. Draws nothing
          unless a room is open in here. */}
      <ListeningSeats />
      {/* The room's own conversation. Draws nothing and moves nobody — it
          publishes one line at a time and the residents above turn to each other
          and wear the bubble. Mounted beside them because it is scoped to the
          same room and dies with it. */}
      <EnsembleDirector roomId={roomId} />
      {/* THE WAY OUT. A room's portals live in the same space its residents do —
          inside the room group, outside the scale group — because a trigger is a
          volume a person walks into, and it has to stay the size of a doorway
          rather than shrink with the building's fit.

          This is what makes the underground transit rather than a dead end: the
          platform's exit portal targets the station at the FAR side of the city,
          so you surface somewhere you did not start. */}
      <Portals />
      {/* AD SURFACES, IN THE ROOM'S OWN SPACE. Rendered here rather than in the
          world-level PlacementLayer, and that placement in the scene graph IS the
          storage decision: a room-scoped placement is saved relative to this
          group, so the group's transform is what puts it back. Nudge the room's
          Y, re-anchor it to another building, turn it — the ads come along,
          because nothing ever wrote down where they were in the world.
          Same space as the residents and portals above, and outside the scale
          group for the same reason: an ad is sized in world units against the
          person looking at it, not against the building's fit. */}
      <RoomPlacements roomId={roomId} />
      {/* "This one, here" — the layer the stylist will point with. */}
      <RoomMarkers roomId={roomId} model={model} />
      {/* A café's floor (rooms.js `tables`): which chairs offer a seat, your
          order and the cup. Same space as the seat buttons and the residents. */}
      {room.tables?.length > 0 && <CafeCounter roomId={roomId} />}
      {/* A pair of headphones on every table (rooms.js `headphones`): the
          spoken-word pieces performed here, for whoever puts them on. */}
      {room.headphones && room.tables?.length > 0 && <TableHeadphones roomId={roomId} />}
      {/* An artist the city seated at a table (API CafeGuests): drawn in the
          chair; sit across and the conversation opens, just the two of you. */}
      {room.tables?.length > 0 && <CafeGuests roomId={roomId} model={model} />}
      {/* A shop's counter (rooms.js `counter`, 2026-10-30, the Corner Market's
          register): what the job rang up, bagged on it. Same space as the cup. */}
      {room.counter && <CounterItems roomId={roomId} />}
      {/* THE GANGSTAFUEL GYM (2026-10-31): its machines used like chairs (a
          treadmill to run on, a bench to press from) and the can on its fuel
          bar. Reads the machines off the model's own `use_*` nodes. */}
      {room.fuel && <GymFloor roomId={roomId} model={model} k={placeScale * fitTune} />}
      {/* A brand on the wall (rooms.js `decals`): the gym's logo, HQ's. */}
      {room.decals?.length > 0 && <RoomDecals roomId={roomId} k={placeScale * fitTune} />}
      {/* Whoever stands at the front desk (rooms.js `frontDesk`: HQ's assistant). */}
      {room.frontDesk && <FrontDesk roomId={roomId} k={placeScale * fitTune} />}
      {room.frontDesk && room.greeting && <RoomGreeting roomId={roomId} k={placeScale * fitTune} />}
      {room.printshop && <PrintShopScreen roomId={roomId} k={placeScale * fitTune} />}
      {/* HYPE-U (city/hypeu): the elevator's doors and car, a classroom's
          instructor and board, and chairs that offer themselves (rooms.js
          `elevator`, `course`, `seats`). */}
      {room.elevator && <Elevator roomId={roomId} k={placeScale * fitTune} />}
      {room.course && <Classroom roomId={roomId} k={placeScale * fitTune} />}
      {room.seats?.length > 0 && !room.tables?.length && <NearSeats roomId={roomId} model={model} />}
      {roomId === 'salon' && <SalonMirrorSkin model={model} />}
      <FloorProbe model={model} roomId={roomId} />
      <group scale={placeScale * fitTune}>
        <group position={fit.offset} scale={fit.s}>
          <primitive object={model} />
        </group>
        {/* THE HOST'S MEDIA ON THE WALLS (a venue type's `screens`). INSIDE the
            scale group, unlike the people above: a screen is a fixture of the
            wall it hangs on and has to grow and shrink with it. Authored in room
            space at the type's fit; `ratio` carries them through a refit. */}
        {room.screens?.length > 0 && (
          <VenueScreens
            roomId={roomId}
            screens={room.screens}
            ratio={fit.fitTo && room.screensFitTo ? fit.fitTo / room.screensFitTo : 1}
          />
        )}
      </group>
    </group>
  );
}
